/**
 * Gerador de chaves de idempotência para requisições HTTP
 * 
 * Consolidação da lógica duplicada de geração de idempotency keys
 * Origem: linhas 1124-1139 e 1277-1282 do useSaveController.ts
 */

import { IDEM_KEY_TYPE_SAVE, IDEM_KEY_TYPE_TEMP, IDEM_KEY_MAX_LENGTH } from '../constants/saveConstants';

/**
 * Tipo da chave de idempotência
 */
export type IdempotencyKeyType = 'SAVE' | 'TEMP';

/**
 * Opções para geração da chave
 */
export interface IdempotencyKeyOptions {
  /**
   * Tipo da operação (SAVE para produção, TEMP para temporário)
   */
  type: IdempotencyKeyType;
  
  /**
   * ID do orçamento/pedido
   */
  orcId: number;
  
  /**
   * ID do item do orçamento
   */
  oitId: number;
  
  /**
   * Hash do arquivo (opcional, usado quando disponível)
   * MD5 (32 chars) ou SHA256 (64 chars)
   */
  hash?: string;
  
  /**
   * Timestamp customizado (opcional, usa Date.now() se não fornecido)
   */
  timestamp?: number;
}

/**
 * Gera uma chave de idempotência única para requisições HTTP
 * 
 * Formato com hash: `{TYPE}-{orc_id}-{oit_id}-{hash}`
 * Formato sem hash: `{TYPE}-{orc_id}-{oit_id}-{timestamp}-{uuid_short}`
 * 
 * @param options - Opções de geração
 * @returns Chave de idempotência (máximo 128 caracteres)
 * 
 * @example
 * // Com hash do arquivo
 * const key1 = generateIdempotencyKey({
 *   type: 'SAVE',
 *   orcId: 12345,
 *   oitId: 67890,
 *   hash: 'a1b2c3d4e5f6...'
 * });
 * // Resultado: "SAVE-12345-67890-a1b2c3d4e5f6..."
 * 
 * @example
 * // Sem hash (fallback para timestamp + uuid)
 * const key2 = generateIdempotencyKey({
 *   type: 'TEMP',
 *   orcId: 12345,
 *   oitId: 67890
 * });
 * // Resultado: "TEMP-12345-67890-20251117143025-a1b2c3d4"
 */
export function generateIdempotencyKey(options: IdempotencyKeyOptions): string {
  const { type, orcId, oitId, hash, timestamp } = options;
  
  // Validar inputs
  if (!type || (type !== IDEM_KEY_TYPE_SAVE && type !== IDEM_KEY_TYPE_TEMP)) {
    throw new Error(`Invalid idempotency key type: ${type}`);
  }
  
  if (!Number.isInteger(orcId) || orcId <= 0) {
    throw new Error(`Invalid orc_id: ${orcId}`);
  }
  
  if (!Number.isInteger(oitId) || oitId <= 0) {
    throw new Error(`Invalid oit_id: ${oitId}`);
  }
  
  // Tentar usar hash se fornecido e válido
  if (hash) {
    const cleaned = hash.toString().replace(/[^a-fA-F0-9]/g, '');
    const isValidHash = cleaned.length === 32 || cleaned.length === 64;
    
    if (isValidHash) {
      // Formato com hash: TYPE-{orc}-{oit}-{hash}
      const key = `${type}-${orcId}-${oitId}-${cleaned}`;
      return key.slice(0, IDEM_KEY_MAX_LENGTH);
    }
  }
  
  // Fallback: usar timestamp + uuid curto
  const runTimestamp = timestamp ?? Date.now();
  const runTag = new Date(runTimestamp)
    .toISOString()
    .slice(0, 16)
    .replace(/[:T-]/g, '');
  
  // Gerar UUID curto
  let uuid: string;
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    uuid = crypto.randomUUID();
  } else {
    // Fallback: usar timestamp como "uuid"
    uuid = String(runTimestamp);
  }
  
  // Limpar e encurtar UUID
  const uuidCleaned = uuid.replace(/[^a-zA-Z0-9-]/g, '');
  const uuidShort = uuidCleaned.includes('-') 
    ? uuidCleaned.split('-')[0] 
    : uuidCleaned.slice(0, 8);
  
  // Formato sem hash: TYPE-{orc}-{oit}-{yyyymmddHHmmss}-{uuid_short}
  const key = `${type}-${orcId}-${oitId}-${runTag}-${uuidShort}`;
  return key.slice(0, IDEM_KEY_MAX_LENGTH);
}

/**
 * Valida se uma string é uma chave de idempotência válida
 * 
 * @param key - Chave a validar
 * @returns true se válida, false caso contrário
 */
export function isValidIdempotencyKey(key: string): boolean {
  if (!key || typeof key !== 'string') return false;
  if (key.length > IDEM_KEY_MAX_LENGTH) return false;
  
  // Formato esperado: TYPE-{orc}-{oit}-{hash_ou_timestamp}-{uuid}?
  const parts = key.split('-');
  if (parts.length < 4) return false;
  
  const [type, orcId, oitId] = parts;
  
  // Validar tipo
  if (type !== IDEM_KEY_TYPE_SAVE && type !== IDEM_KEY_TYPE_TEMP) return false;
  
  // Validar números
  if (!Number.isInteger(Number(orcId)) || Number(orcId) <= 0) return false;
  if (!Number.isInteger(Number(oitId)) || Number(oitId) <= 0) return false;
  
  return true;
}

